import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Camera, FileText, Upload, X, ArrowRight, MapPin, ClipboardList, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import { api, formatError } from '../lib/api';
import { useAuth } from '../context/AuthContext';

/**
 * /pn/jobs/:id — Job detail + Photos + Warranty vault
 * Anyone with job access (poster, quoting tradies, awarded tradie) can upload attachments.
 */
export default function JobDetail() {
  const { user, loading: authLoading } = useAuth();
  const { id: jobId } = useParams();
  const navigate = useNavigate();
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/login?redirect=/pn/jobs/${jobId}`);
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, jobId]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.pnListAttachments(jobId);
      setAttachments(r.data || []);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  const openAttachment = async (att) => {
    try {
      const r = await api.pnFetchAttachment(att.id);
      setSelected(r.data);
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  if (!user) return null;

  const photos = attachments.filter((a) => a.kind.startsWith('photo_'));
  const warranties = attachments.filter((a) => a.kind === 'warranty');
  const others = attachments.filter((a) => !a.kind.startsWith('photo_') && a.kind !== 'warranty');

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="job-detail-page">
      <Header />
      <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#1A362D]">Job · Attachments &amp; Warranty Vault</div>
            <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight text-[#1C1B1A]">Photos, documents &amp; warranties</h1>
            <p className="mt-3 max-w-2xl text-sm text-[#5C5B5A]">Everything uploaded here becomes part of the property's permanent Passport.</p>
          </div>
          <button onClick={() => setShowUpload(true)} className="btn-primary" data-testid="upload-attachment-btn">
            <Upload size={14} /> Upload
          </button>
        </div>

        {loading ? (
          <div className="mt-10 border border-dashed border-[#E5E4E0] bg-white p-10 text-center text-sm text-[#8A8988]" data-testid="attachments-loading">Loading…</div>
        ) : attachments.length === 0 ? (
          <div className="mt-10 border border-dashed border-[#E5E4E0] bg-white p-12 text-center" data-testid="attachments-empty">
            <Camera size={32} className="mx-auto text-[#8A8988]" />
            <h3 className="mt-4 font-heading text-xl font-bold text-[#1C1B1A]">Nothing uploaded yet</h3>
            <p className="mt-2 text-sm text-[#5C5B5A]">Add before/after photos, warranty PDFs and job docs. They become part of the Property Passport.</p>
            <button onClick={() => setShowUpload(true)} className="btn-primary mt-6 inline-flex" data-testid="attachments-empty-upload">
              <Upload size={14} /> Upload first file
            </button>
          </div>
        ) : (
          <>
            {photos.length > 0 && (
              <section className="mt-10">
                <h2 className="font-heading text-xl font-bold text-[#1C1B1A]">Photos</h2>
                <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                  {photos.map((p) => (
                    <button key={p.id} onClick={() => openAttachment(p)} className="group border border-[#E5E4E0] bg-white p-3 text-left transition-all hover:border-[#1A362D]" data-testid={`photo-${p.id}`}>
                      <div className="flex h-24 items-center justify-center bg-[#F3F2EE] text-[#8A8988]">
                        <Camera size={24} />
                      </div>
                      <div className="mt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#D96C4E]">{p.kind.replace('photo_', '')}</div>
                      <div className="mt-1 truncate text-xs font-medium text-[#1C1B1A]">{p.filename}</div>
                      {p.caption && <div className="mt-1 truncate text-[11px] text-[#5C5B5A]">{p.caption}</div>}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {warranties.length > 0 && (
              <section className="mt-10">
                <h2 className="font-heading text-xl font-bold text-[#1C1B1A]">Warranty vault</h2>
                <div className="mt-3 space-y-2">
                  {warranties.map((w) => (
                    <button key={w.id} onClick={() => openAttachment(w)} className="flex w-full items-center justify-between border border-[#E5E4E0] bg-white p-4 text-left transition-colors hover:border-[#1A362D]" data-testid={`warranty-${w.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center bg-[#1A362D] text-white">
                          <ShieldCheck size={16} />
                        </div>
                        <div>
                          <div className="font-semibold text-[#1C1B1A]">{w.filename}</div>
                          {w.caption && <div className="mt-0.5 text-xs text-[#5C5B5A]">{w.caption}</div>}
                        </div>
                      </div>
                      <div className="text-xs text-[#5C5B5A]">
                        {w.warranty_expires_on ? `Expires ${new Date(w.warranty_expires_on).toLocaleDateString('en-AU')}` : 'No expiry set'}
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            )}

            {others.length > 0 && (
              <section className="mt-10">
                <h2 className="font-heading text-xl font-bold text-[#1C1B1A]">Other documents</h2>
                <div className="mt-3 space-y-2">
                  {others.map((o) => (
                    <button key={o.id} onClick={() => openAttachment(o)} className="flex w-full items-center gap-3 border border-[#E5E4E0] bg-white p-3 text-left hover:border-[#1A362D]" data-testid={`doc-${o.id}`}>
                      <FileText size={16} className="text-[#5C5B5A]" />
                      <div className="flex-1 truncate text-sm font-medium text-[#1C1B1A]">{o.filename}</div>
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8988]">{o.kind}</div>
                    </button>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </div>

      {showUpload && <UploadModal jobId={jobId} onClose={() => setShowUpload(false)} onUploaded={() => { setShowUpload(false); load(); }} />}
      {selected && <PreviewModal att={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function UploadModal({ jobId, onClose, onUploaded }) {
  const [form, setForm] = useState({
    kind: 'photo_after',
    filename: '',
    mime_type: '',
    data_b64: '',
    caption: '',
    warranty_expires_on: '',
  });
  const [busy, setBusy] = useState(false);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('File must be under 8 MB.');
      return;
    }
    const r = new FileReader();
    r.onload = () => {
      const raw = String(r.result);
      const b64 = raw.split(',')[1] || '';
      setForm((f) => ({ ...f, filename: file.name, mime_type: file.type, data_b64: b64 }));
    };
    r.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.data_b64) {
      toast.error('Please choose a file to upload.');
      return;
    }
    setBusy(true);
    try {
      await api.pnUploadAttachment(jobId, {
        job_id: jobId,
        kind: form.kind,
        filename: form.filename,
        mime_type: form.mime_type,
        data_b64: form.data_b64,
        caption: form.caption || undefined,
        warranty_expires_on: (form.kind === 'warranty' && form.warranty_expires_on) ? form.warranty_expires_on : undefined,
      });
      toast.success('Attachment uploaded — Property Passport updated.');
      onUploaded();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4" data-testid="upload-modal">
      <div className="w-full max-w-lg border border-[#1C1B1A] bg-white">
        <div className="flex items-center justify-between border-b border-[#E5E4E0] px-6 py-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">Job · Upload</div>
            <h3 className="font-heading text-xl font-bold text-[#1C1B1A]">Add a photo, warranty or doc</h3>
          </div>
          <button onClick={onClose} className="text-[#8A8988] hover:text-[#1C1B1A]" data-testid="close-upload"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4 p-6">
          <label className="block">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#5C5B5A]">Type</div>
            <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="input-base" data-testid="upload-kind">
              <option value="photo_before">Photo — before</option>
              <option value="photo_during">Photo — during</option>
              <option value="photo_after">Photo — after</option>
              <option value="warranty">Warranty document</option>
              <option value="invoice_doc">Invoice / receipt</option>
              <option value="other">Other document</option>
            </select>
          </label>
          <label className="block cursor-pointer border border-dashed border-[#1A362D] bg-[#FAF9F6] p-6 text-center" data-testid="upload-dropzone">
            <input type="file" onChange={handleFile} className="hidden" accept="image/*,application/pdf" data-testid="upload-file-input" />
            <Upload size={20} className="mx-auto text-[#1A362D]" />
            <div className="mt-2 text-sm font-semibold text-[#1C1B1A]">
              {form.filename ? `✓ ${form.filename}` : 'Choose a file (max 8 MB)'}
            </div>
            <div className="mt-1 text-xs text-[#8A8988]">Images or PDF</div>
          </label>
          <input
            value={form.caption}
            onChange={(e) => setForm({ ...form, caption: e.target.value })}
            placeholder="Caption (optional)"
            maxLength={280}
            className="input-base"
            data-testid="upload-caption"
          />
          {form.kind === 'warranty' && (
            <label className="block">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#5C5B5A]">Warranty expiry (optional but recommended)</div>
              <input
                type="date"
                value={form.warranty_expires_on}
                onChange={(e) => setForm({ ...form, warranty_expires_on: e.target.value })}
                className="input-base"
                data-testid="upload-warranty-expiry"
              />
            </label>
          )}
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="upload-submit">
            {busy ? 'Uploading…' : 'Upload'} <ArrowRight size={14} />
          </button>
        </form>
      </div>
    </div>
  );
}

function PreviewModal({ att, onClose }) {
  const isImage = att.mime_type?.startsWith('image/');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="preview-modal">
      <div className="w-full max-w-3xl border border-[#1C1B1A] bg-white">
        <div className="flex items-center justify-between border-b border-[#E5E4E0] px-6 py-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#D96C4E]">{att.kind}</div>
            <h3 className="font-heading text-lg font-bold text-[#1C1B1A]">{att.filename}</h3>
          </div>
          <button onClick={onClose} className="text-[#8A8988] hover:text-[#1C1B1A]" data-testid="close-preview"><X size={18} /></button>
        </div>
        <div className="p-6">
          {isImage ? (
            <img src={att.data_url} alt={att.filename} className="mx-auto max-h-[70vh] w-auto" data-testid="preview-image" />
          ) : (
            <a href={att.data_url} download={att.filename} className="btn-primary inline-flex" data-testid="preview-download">
              <FileText size={14} /> Download {att.filename}
            </a>
          )}
          {att.caption && <div className="mt-4 text-sm text-[#5C5B5A]">{att.caption}</div>}
          {att.warranty_expires_on && (
            <div className="mt-3 text-xs text-[#8A8988]">Warranty expires: {new Date(att.warranty_expires_on).toLocaleDateString('en-AU')}</div>
          )}
        </div>
      </div>
    </div>
  );
}
